import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';

type GlossyVariant = 'navy' | 'orange';

const VARIANT_STOPS: Record<
  GlossyVariant,
  { top: string; mid: string; base: string; edge: string; shadow: string }
> = {
  navy: {
    top: '#3d4f8c',
    mid: '#253060',
    base: brandColors.navy,
    edge: '#070b1c',
    shadow: 'rgba(17, 23, 57, 0.45)',
  },
  orange: {
    top: '#ff8c4d',
    mid: '#f06820',
    base: brandColors.orange,
    edge: '#9a3a08',
    shadow: 'rgba(232, 88, 11, 0.45)',
  },
};

export interface GlossyActionButtonProps {
  label: string;
  /** Shown when `pressed` is true */
  pressedLabel?: string;
  onClick?: () => void;
  variant?: GlossyVariant;
  /** Persistent submitted / pressed appearance */
  pressed?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  type?: 'button' | 'submit';
}

/**
 * Pill-shaped 3D glossy action button — sports assessment UI (not gaming flat buttons).
 */
const GlossyActionButton: React.FC<GlossyActionButtonProps> = ({
  label,
  pressedLabel,
  onClick,
  variant = 'orange',
  pressed = false,
  disabled = false,
  fullWidth = false,
  startIcon,
  type = 'button',
}) => {
  const [activePress, setActivePress] = useState(false);
  const colors = VARIANT_STOPS[variant];
  const isDown = pressed || activePress;
  const displayLabel = pressed && pressedLabel ? pressedLabel : label;

  const releasePress = useCallback(() => setActivePress(false), []);

  return (
    <Box
      component="button"
      type={type}
      disabled={disabled || pressed}
      aria-pressed={pressed}
      onClick={pressed ? undefined : onClick}
      onMouseDown={() => !pressed && !disabled && setActivePress(true)}
      onMouseUp={releasePress}
      onMouseLeave={releasePress}
      onTouchStart={() => !pressed && !disabled && setActivePress(true)}
      onTouchEnd={releasePress}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        width: fullWidth ? '100%' : 'auto',
        minHeight: 44,
        px: 2.5,
        py: 1.1,
        border: 'none',
        borderRadius: '999px',
        cursor: pressed || disabled ? 'default' : 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        fontFamily: '"Inter", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '0.95rem',
        letterSpacing: 0.4,
        textTransform: 'none',
        color: brandColors.white,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
        transform: isDown ? 'translateY(3px) scale(0.995)' : 'translateY(0)',
        background: isDown
          ? `linear-gradient(180deg, ${colors.base} 0%, ${colors.edge} 100%)`
          : `linear-gradient(180deg, ${colors.top} 0%, ${colors.mid} 38%, ${colors.base} 72%, ${colors.edge} 100%)`,
        boxShadow: isDown
          ? `inset 0 3px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 2px ${colors.shadow}`
          : `inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.18), 0 4px 0 ${colors.edge}, 0 7px 14px ${colors.shadow}`,
        filter: pressed ? 'saturate(0.88) brightness(0.92)' : 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '8%',
          right: '8%',
          height: '42%',
          borderRadius: '999px',
          background: isDown
            ? 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.08) 55%, transparent 100%)',
          pointerEvents: 'none',
        },
        '&:focus-visible': {
          outline: `3px solid ${brandColors.blue}`,
          outlineOffset: 2,
        },
        '&:disabled': {
          opacity: pressed ? 1 : 0.55,
        },
      }}
    >
      {startIcon && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', zIndex: 1 }}>
          {startIcon}
        </Box>
      )}
      <Typography
        component="span"
        sx={{
          zIndex: 1,
          fontWeight: 800,
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          color: 'inherit',
          lineHeight: 1,
        }}
      >
        {displayLabel}
      </Typography>
    </Box>
  );
};

export default GlossyActionButton;
